/*
 * Build artifacts tab: pure model, no React. The icon of each kind, the facts line under the file name, whether the
 * signing line shows, and the metadata read from the binary under the names each platform uses.
 */
import type { Artifact } from '@/api/schemas';
import { formatBytes, formatDateTime } from '@/lib/format';

const KIND_ICON: Record<string, string> = { aab: 'android', apk: 'android', ipa: 'phone_iphone', app_zip: 'folder_zip', app_tar_gz: 'folder_zip', dsym: 'description', mapping: 'description', sourcemap: 'description', log: 'terminal', metadata: 'description', sbom: 'shield' };

export const kindIcon = (kind: string): string => KIND_ICON[kind] ?? 'description';

/** The Android binaries get the Android tint. */
export const isAndroidBinary = (kind: string): boolean => kind === 'aab' || kind === 'apk';

/** `appMetadata` keys under the names each platform uses (SPEC §27); unknown keys keep their own name. */
const ANDROID_KINDS = new Set(['apk', 'aab', 'mapping']);
const IOS_KINDS = new Set(['ipa', 'app_zip', 'app_tar_gz', 'dsym']);
const LABELS = {
  android: { bundleId: 'package', versionName: 'versionName', buildNumber: 'versionCode', minSdk: 'minSdk', targetSdk: 'targetSdk' } as Record<string, string>,
  ios: { bundleId: 'bundle id', versionName: 'version', buildNumber: 'build' } as Record<string, string>,
};
const METADATA_ORDER = ['bundleId', 'versionName', 'buildNumber', 'minSdk', 'targetSdk'];

const labelsFor = (kind: string): Record<string, string> => {
  if (ANDROID_KINDS.has(kind)) return LABELS.android;
  return IOS_KINDS.has(kind) ? LABELS.ios : {};
};

const metadataRank = (key: string): number => (METADATA_ORDER.includes(key) ? METADATA_ORDER.indexOf(key) : METADATA_ORDER.length);

/** The metadata read from the binary as label/value rows: well-known keys first, empty values dropped, objects as JSON. */
export function metadataRows(a: Pick<Artifact, 'kind' | 'appMetadata'>): [string, string][] {
  const labels = labelsFor(a.kind);
  return Object.entries(a.appMetadata ?? {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .sort(([x], [y]) => metadataRank(x) - metadataRank(y))
    .map(([k, v]) => [labels[k] ?? k, typeof v === 'object' ? JSON.stringify(v) : String(v)]);
}

type FactsInput = Pick<Artifact, 'sizeBytes' | 'storageClass' | 'mirroredAt' | 'deletedAt' | 'retentionUntil' | 'pinned'>;

/** A storage class other than STANDARD is worth a mention, but not on a file that is gone. */
const storageFact = (a: FactsInput): string | null => (!a.deletedAt && a.storageClass && a.storageClass !== 'STANDARD' ? a.storageClass : null);

/** When it was deleted, else when retention will delete it (never for a pinned artifact). */
function retentionFact(a: FactsInput): string | null {
  if (a.deletedAt) return `deleted ${formatDateTime(a.deletedAt)}`;
  return a.retentionUntil && !a.pinned ? `expires ${formatDateTime(a.retentionUntil)}` : null;
}

/** The facts line: size, storage class, when it was mirrored and when it was or will be deleted. */
export function artifactFacts(a: FactsInput): string[] {
  const facts = [formatBytes(a.sizeBytes), storageFact(a), a.mirroredAt ? `mirrored ${formatDateTime(a.mirroredAt)}` : null, retentionFact(a)];
  return facts.filter((fact): fact is string => Boolean(fact));
}

/** The signing line shows when the binary carries a certificate fingerprint or subject. */
export const hasSigning = (a: Pick<Artifact, 'signing'>): boolean => Boolean(a.signing?.sha1 || a.signing?.subject);
